import React, { useState } from 'react';
import { Upload, Palette, Eye, Save, AlertCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useBranding } from '../hooks/useBranding';
import BrandLogo from '../components/BrandLogo';

const BrandingSettings: React.FC = () => {
  const { t } = useTranslation(['settings', 'common']);
  const { brandConfig, loading, error, uploadLogo, updateTheme } = useBranding();
  const [primaryColor, setPrimaryColor] = useState(brandConfig?.primary_color || '#2E7D32');
  const [uploading, setUploading] = useState(false);
  const [updating, setUpdating] = useState(false);

  const handleLogoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const logoFile = files[0];
    const validTypes = ['image/png', 'image/svg+xml', 'image/jpeg'];
    
    if (!validTypes.includes(logoFile.type)) {
      toast.error(t('settings:invalidFileType'));
      return;
    }

    if (logoFile.size > 1024 * 1024) { // 1MB
      toast.error(t('settings:fileTooLarge'));
      return;
    }

    try {
      setUploading(true);
      await uploadLogo(logoFile);
      toast.success(t('settings:branding.logoUploaded'));
      event.target.value = ''; // Reset input
    } catch (err) {
      toast.error(t('settings:branding.errorUpload'));
    } finally {
      setUploading(false);
    }
  };

  const handleColorUpdate = async () => {
    if (!primaryColor || !primaryColor.match(/^#[0-9A-F]{6}$/i)) {
      toast.error(t('settings:invalidHexColor'));
      return;
    }

    try {
      setUpdating(true);
      await updateTheme(primaryColor);
      toast.success(t('settings:branding.themeUpdated'));
    } catch (err) {
      toast.error(t('settings:branding.errorTheme'));
    } finally {
      setUpdating(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-8 bg-[var(--gray-200)] rounded w-1/4 mb-4"></div>
          <div className="space-y-4">
            <div className="h-32 bg-[var(--gray-200)] rounded"></div>
            <div className="h-24 bg-[var(--gray-200)] rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-heading-3 text-[var(--text-primary)] mb-2">
          {t('settings:branding.title')}
        </h2>
        <p className="text-body text-[var(--text-secondary)]">
          {t('settings:branding.description')}
        </p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start space-x-3">
          <AlertCircle className="w-5 h-5 text-red-500 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-red-800">{t('common:error')}</p>
            <p className="text-sm text-red-700">{error === 'branding:errorLoad' ? t('settings:branding.errorLoad') : error === 'branding:errorUpload' ? t('settings:branding.errorUpload') : error === 'branding:errorTheme' ? t('settings:branding.errorTheme') : t('settings:branding.errorGeneric')}</p>
          </div>
        </div>
      )}

      {/* Current Branding Preview */}
      <div className="card">
        <h3 className="text-heading-4 text-[var(--text-primary)] mb-4">
          {t('settings:branding.currentBranding')}
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h4 className="text-sm font-medium text-[var(--text-primary)] mb-3">
              {t('settings:branding.lightMode')}
            </h4>
            <div className="bg-white border border-[var(--border-light)] rounded-lg p-6 flex items-center justify-center">
              <BrandLogo size="lg" />
            </div>
          </div>
          
          <div>
            <h4 className="text-sm font-medium text-[var(--text-primary)] mb-3">
              {t('settings:branding.darkMode')}
            </h4>
            <div className="bg-gray-900 border border-[var(--border-light)] rounded-lg p-6 flex items-center justify-center">
              <div data-theme="dark">
                <BrandLogo size="lg" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Logo Upload */}
      <div className="card">
        <h3 className="text-heading-4 text-[var(--text-primary)] mb-4">
          {t('settings:branding.logoUpload')}
        </h3>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">
              {t('settings:branding.organizationLogo')}
            </label>
            <div className="flex items-center space-x-4">
              <input
                type="file"
                accept=".png,.svg,.jpg,.jpeg"
                onChange={handleLogoUpload}
                className="hidden"
                id="logo-upload"
                disabled={uploading}
              />
              <label
                htmlFor="logo-upload"
                className={`flex items-center px-4 py-2 border border-[var(--border-medium)] rounded-lg cursor-pointer hover:bg-[var(--gray-50)] transition-colors ${
                  uploading ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                <Upload className="w-4 h-4 mr-2" />
                {uploading ? t('common:uploading') : t('common:upload')}
              </label>
            </div>
            <p className="text-xs text-[var(--text-secondary)] mt-1">
              {t('settings:branding.logoRequirements')}
            </p>
          </div>
        </div>
      </div>

      {/* Color Theme */}
      <div className="card">
        <h3 className="text-heading-4 text-[var(--text-primary)] mb-4">
          {t('settings:branding.colorTheme')}
        </h3>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">
              {t('settings:branding.primaryColor')}
            </label>
            <div className="flex items-center space-x-4">
              <div className="relative">
                <input
                  type="color"
                  value={primaryColor}
                  onChange={(e) => setPrimaryColor(e.target.value)}
                  className="w-12 h-10 border border-[var(--border-medium)] rounded cursor-pointer"
                />
                <Palette className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-4 h-4 text-white pointer-events-none" />
              </div>
              <input
                type="text"
                value={primaryColor}
                onChange={(e) => setPrimaryColor(e.target.value)}
                className="input-field flex-1 max-w-32"
                placeholder="#2E7D32"
                pattern="^#[0-9A-Fa-f]{6}$"
              />
              <button
                onClick={handleColorUpdate}
                disabled={updating || primaryColor === brandConfig?.primary_color}
                className="btn-primary flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Save className="w-4 h-4" />
                <span>{updating ? t('common:updating') : t('common:save')}</span>
              </button>
            </div>
            <p className="text-xs text-[var(--text-secondary)] mt-1">
              {t('settings:branding.colorRequirements')}
            </p>
          </div>
        </div>
      </div>

      {/* Preview */}
      <div className="card">
        <h3 className="text-heading-4 text-[var(--text-primary)] mb-4">
          {t('settings:branding.livePreview')}
        </h3>
        
        <div className="space-y-4">
          <div className="p-4 bg-[var(--gray-50)] rounded-lg">
            <div className="flex items-center space-x-4 mb-4">
              <Eye className="w-5 h-5 text-[var(--text-secondary)]" />
              <span className="text-sm font-medium text-[var(--text-primary)]">
                {t('settings:branding.headerPreview')}
              </span>
            </div>
            <div className="bg-white border border-[var(--border-light)] rounded p-4">
              <BrandLogo size="md" />
            </div>
          </div>
          
          <div className="grid grid-cols-3 gap-4">
            <div 
              className="h-8 rounded" 
              style={{ backgroundColor: primaryColor }}
              title={t('settings:branding.primaryColorPreview', { color: primaryColor })}
            />
            <div 
              className="h-8 rounded bg-[var(--color-secondary)]" 
              title={t('settings:branding.secondaryColorPreview')}
            />
            <div
              className="h-8 rounded bg-[var(--color-accent)]"
              title={t('settings:branding.accentColorPreview')}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default BrandingSettings;